import {
  Directive,
  forwardRef,
  input,
  InputSignal,
  effect
} from '@angular/core';
import {
  NG_VALIDATORS
} from '@angular/forms';
import type {
  Provider,
  Type
} from '@angular/core';
import type {
  AbstractControl,
  ValidationErrors,
  Validator
} from '@angular/forms';
import type { SelectedFile } from './file-upload.component';

export const FILE_UPLOAD_EXTENSION_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef((): Type<FileUploadExtensionValidator> => FileUploadExtensionValidator),
  multi: true
};

/**
 * tdWbFileUploadExtensionValidator Directive
 *
 * Validates the types of all files added in a file input
 *
 * This validator is useful on cases where you allow drag and drop,
 * since the "accept" attribute would only filter the files on the dialog
 * but not prevent them from being added.
 *
 * @example
 * <td-wb-file-upload [tdWbFileUploadExtensionValidator]="['application/pdf', 'image/jpeg']"></td-wb-file-upload>
 */
@Directive({
  selector: '[tdWbFileUploadExtensionValidator][ngModel]',
  providers: [FILE_UPLOAD_EXTENSION_VALIDATOR]
})
export class FileUploadExtensionValidator implements Validator {
  private onChange: () => void;
  readonly types: InputSignal<Array<string> | undefined> = input<Array<string> | undefined>(undefined, { alias: 'tdWbFileUploadExtensionValidator' });
  private _prevTypes: Array<string> | undefined;

  constructor() {
    effect(() => {
      const t = this.types();
      if (t !== this._prevTypes && this.onChange !== undefined) {
        this.onChange();
      }
      this._prevTypes = t;
    });
  }

  validate(fileUploadControl: AbstractControl): ValidationErrors | null {
    const types = this.types();
    if (types !== undefined && fileUploadControl.value !== undefined && fileUploadControl.value !== null) {
      return this.validateExtensions(fileUploadControl.value, types);
    }
    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onChange = fn;
  }

  private validateExtensions(files: Array<SelectedFile>, types: Array<string>): ValidationErrors | null {
    for (const file of files) {
      if (types.indexOf(file.file?.type) === -1) {
        return { EXTENSION: true };
      }
    }
    return null;
  }
}
